import { memo, useEffect, useRef, useState } from 'react'
import { CSSProperties } from 'react'

import Download from '@carbon/icons-react/lib/Download'
import {
  Partner,
  CustomerFindInvoiceFragment,
  useCustomerGetInvoicePdfLinkLazyQuery,
  InvoicePdfStatus
} from 'graph/generated/payments/graphql-types'
import Text from 'lib/ui/Text'
import ToastMessage from 'lib/ui/Toast/v2'
import toast from 'react-hot-toast'
import { IReactToPrintProps, useReactToPrint } from 'react-to-print'
import { actionTracking, errorTracking } from 'utils/posthog'

import { useCompanyAndInvoiceContext } from '@/hooks/providers/CompanyAndInvoiceProvider'
import Button, { IButtonProps } from '@/ui/Button/v2'

import { formatIcon } from './styles'
import InvoicePdfLayoutV2 from './v2/InvoicePdfLayout'
import * as css from './v2/styles'

const START_INTERVAL = 5_000 // 5 seconds
const EXPONENTIAL_RATE = 2
const MAX_ATTEMPTS = 5

interface IInvoiceDownload {
  autoDownload?: boolean
  autoDownloadWithButton?: boolean
  btnProps?: IButtonProps
  rootStyles?: CSSProperties
  btnText?: string
  invoice: CustomerFindInvoiceFragment
  onDownloaded?: () => void
  showLink?: boolean
  showIcon?: boolean
  buttonSize?: 'sm' | 'md' | 'lg'
  ignoreMargin?: boolean
  buttonIcon?: JSX.Element
}

const InvoiceDownload = memo(
  ({
    autoDownload = false,
    autoDownloadWithButton = false,
    btnText,
    invoice,
    btnProps,
    rootStyles,
    onDownloaded,
    showLink = false,
    showIcon = true,
    buttonSize = 'md',
    ignoreMargin = false,
    buttonIcon
  }: IInvoiceDownload) => {
    // print
    const componentRef = useRef(null)
    const handlePrintProps: IReactToPrintProps = {
      content: () => componentRef.current
    }
    handlePrintProps.pageStyle = css.reactToPrintPageStyle
    const handlePrint = useReactToPrint(handlePrintProps)

    // partner data
    const partner = invoice?.partner as Partner
    const { brandColor, company } = useCompanyAndInvoiceContext()
    const allowPDFFallback = company?.pdfFallbackEnabled ?? true

    // fetch
    const [isPolling, setIsPolling] = useState(false)
    const [attempts, setAttempts] = useState(0)
    const [fetchInvoiceDownload, { data, loading: loadingInvoice, error }] =
      useCustomerGetInvoicePdfLinkLazyQuery({
        variables: { invoiceID: invoice?.id },
        fetchPolicy: 'cache-and-network'
      })

    // handle fetch response
    useEffect(() => {
      if (loadingInvoice || (data === undefined && error === undefined)) return

      if (error) {
        if (error?.message === 'Internal Server Error') {
          toast.error(
            <ToastMessage
              type='error'
              title='Invoice PDF is not yet synchronized'
              subtitle='Please try again shortly'
            />,
            { id: 'error' }
          )
          return
        }

        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        errorTracking({
          fingerprint: 'InvoiceDownload',
          error: error,
          properties: {
            invoiceID: invoice?.id,
            partnerID: partner?.id,
            allowPDFFallback
          }
        })
        return
      }

      const status = data?.CustomerGetInvoicePdfLink?.status
      const url = data?.CustomerGetInvoicePdfLink?.url

      if (
        [InvoicePdfStatus.Pending, InvoicePdfStatus.Expired].includes(status)
      ) {
        // retry backoff
        setTimeout(
          () => setAttempts(prev => prev + 1),
          START_INTERVAL * Math.pow(EXPONENTIAL_RATE, attempts)
        )
        actionTracking({
          action: 'InvoiceDownloadRetry',
          properties: {
            invoice,
            partnerID: partner?.id,
            data,
            allowPDFFallback
          }
        })

        return
      }

      actionTracking({
        action: 'InvoiceDownloaded',
        properties: {
          invoice,
          partnerID: partner?.id,
          data,
          allowPDFFallback
        }
      })

      if (status === InvoicePdfStatus.Failure) {
        setIsPolling(false)

        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        return
      }

      if (status === InvoicePdfStatus.Success) {
        setIsPolling(false)

        if (url) {
          window.open(url, '_blank')
          if (onDownloaded) onDownloaded()
        } else {
          if (allowPDFFallback) {
            handlePrint()
          } else {
            toast.error(
              <ToastMessage
                type='error'
                title='Invoice PDF unavailable'
                subtitle={`Please contact ${company?.name} for more details.`}
              />,
              { id: 'error' }
            )
          }
        }
        return
      }

      if (allowPDFFallback) {
        // fallback to print
        handlePrint()
      } else {
        toast.error(
          <ToastMessage
            type='error'
            title='Invoice PDF unavailable'
            subtitle={`Please contact ${company?.name} for more details.`}
          />,
          { id: 'error' }
        )
      }
    }, [data, loadingInvoice, error])

    const handleDownload = async () => {
      try {
        setIsPolling(true)
        await fetchInvoiceDownload()
      } catch (error) {
        errorTracking({
          fingerprint: 'InvoiceDownload',
          error: error,
          properties: {
            invoiceID: invoice?.id,
            partnerID: partner?.id
          }
        })
      }
    }

    useEffect(() => {
      if (!attempts) {
        return
      }
      if (attempts >= MAX_ATTEMPTS) {
        setIsPolling(false)
        setAttempts(0)
        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        return
      }

      handleDownload()
    }, [attempts])

    useEffect(() => {
      if (autoDownload || autoDownloadWithButton) {
        handleDownload()
      }
    }, [autoDownload, autoDownloadWithButton])

    return (
      <>
        <div css={{ ...rootStyles }}>
          {(!autoDownload || autoDownloadWithButton) && (
            <Button
              onClick={handleDownload}
              loading={loadingInvoice || isPolling}
              variant='outline'
              buttonSize={buttonSize}
              iconPlacement='left'
              display='flex'
              {...btnProps}
            >
              {showIcon && (
                <span
                  css={{
                    ...(ignoreMargin
                      ? { '& svg': { margin: 0 } }
                      : { formatIcon })
                  }}
                >
                  {buttonIcon || <Download size={16} />}
                </span>
              )}
              {btnText && <Text>{btnText}</Text>}
            </Button>
          )}
          {showLink && data?.CustomerGetInvoicePdfLink?.url && (
            <Text size='sm' margin={[0.5, 0, 0]}>
              If you have trouble downloading the invoice you can click {''}
              <a href={data?.CustomerGetInvoicePdfLink?.url}>this link</a>.
            </Text>
          )}
        </div>

        <div ref={componentRef} css={css.showOnlyOnPrint}>
          {invoice && partner && (
            <InvoicePdfLayoutV2
              partner={partner}
              invoice={invoice}
              brandColor={brandColor}
            />
          )}
        </div>
      </>
    )
  }
)

InvoiceDownload.displayName = 'InvoiceDownload'

export default InvoiceDownload
